/**
 * views/Kitchen/KitchenBoard.js
 * ─────────────────────────────────────────────────────────────────────────────
 * THE kitchen display. A wall screen, a tablet on the pass, or a browser tab
 * on the manager's laptop.
 *
 * ## Design rules this screen follows
 *
 * 1. **One glance answers one question: what do I cook next.** Tickets are
 *    columns by stage, oldest first, edge-coloured by how late they are. No
 *    sorting controls, no pagination, no alphabetical anything.
 * 2. **Nothing important is carried by colour alone.** Every ticket also shows
 *    its stage label, its age in words and its position on the board.
 * 3. **The board never lies about freshness.** Ages tick from a server-clock
 *    offset measured at load, so a slow client clock cannot make a late ticket
 *    look fine, and a stalled socket shows as a stale "updated Xs ago".
 * 4. **Realtime first, polling as the safety net.** Socket events patch state
 *    in place for instant feel; a 20 second reconcile poll repairs anything a
 *    dropped frame lost. A kitchen that silently stops updating is worse than
 *    one that flickers.
 * 5. **Every correction is a reason.** Reject, recall and void all open a
 *    reason prompt, because the server requires one and because the audit
 *    trail is only worth having if it says why.
 *
 * The kitchen state machine lives on the server (constants/kitchen.js). This
 * screen never invents a transition: it renders `nextStatus` and `recallTo`
 * exactly as the server projected them.
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { Modal, Button, Form } from "react-bootstrap";
import { toast } from "react-toastify";
import {
  MdRefresh,
  MdFullscreen,
  MdFullscreenExit,
  MdVolumeUp,
  MdVolumeOff,
  MdUndo,
  MdClose,
  MdInfoOutline,
  MdPushPin,
  MdCheckCircle,
  MdRadioButtonUnchecked,
  MdWarningAmber,
  MdRestaurant,
  MdSwapHoriz,
} from "react-icons/md";
import { socket } from "../../utilities/socket";
import useOrderAlerts from "../../hooks/useOrderAlerts";
import newOrderBeep from "../../assets/img/restaurant_notification_beep.mp3";
import {
  apiKitchenList,
  apiKitchenBoard,
  apiKitchenBumpTicket,
  apiKitchenTransitionTicket,
  apiKitchenRecallTicket,
  apiKitchenTransitionItem,
  apiKitchenPrioritiseTicket,
} from "../../api";
import KitchenTicketDrawer from "./KitchenTicketDrawer";
import {
  KDS,
  STATUS_META,
  SLA_META,
  ITEM_STATUS_META,
  ageColour,
  pipFor,
  NUM_DISPLAY,
  MICRO_LABEL,
  formatDuration,
  formatClock,
  describeOptions,
  stationColour,
  formatMoney,
  KITCHEN_REJECT_REASONS,
  KITCHEN_VOID_REASONS,
} from "./kitchenUi";
import AppSelect from "../../components/ui/AppSelect";
import { useBranchScope } from "../../context/BranchScopeContext";

/**
 * Lane order, left to right. Mirrors KITCHEN_LANES on the server. Restated
 * here rather than imported because CRA's ModuleScopePlugin refuses imports
 * from above frontend/src, and a build-breaking import is a worse coupling
 * than a six-line constant.
 */
const LANES = [
  { key: "NEW", label: "New" },
  { key: "ACKNOWLEDGED", label: "Accepted" },
  { key: "IN_PREP", label: "Preparing" },
  { key: "READY", label: "Ready" },
  { key: "HANDED_OFF", label: "Handed off" },
];

const POLL_MS = 20000;
const TICK_MS = 1000;

/**
 * Failures that will never fix themselves on a retry. Polling through one of
 * these just stacks up identical 4xx responses: the first build of this screen
 * fired /board every 20 seconds forever against a 400 VENDOR_REQUIRED, filling
 * the network log with dozens of identical failures while showing the operator
 * a dead end. Stop, and say what to do instead.
 */
const TERMINAL_ERROR_CODES = ["VENDOR_REQUIRED", "NO_KITCHEN", "STAFF_ONLY", "NOT_AUTHENTICATED"];

/** Board sort, applied client-side so a socket patch re-orders instantly. */
const byPriorityThenAge = (a, b) => {
  const pa = Number(a?.priority) || 0;
  const pb = Number(b?.priority) || 0;
  if (pa !== pb) return pb - pa;
  return new Date(a?.placedAt).getTime() - new Date(b?.placedAt).getTime();
};

function Pill({ children, colour, solid, title }) {
  return (
    <span
      title={title}
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 4,
        padding: "2px 9px",
        borderRadius: 999,
        fontSize: 11,
        fontWeight: 700,
        letterSpacing: 0.3,
        textTransform: "uppercase",
        color: solid ? "#08111f" : colour,
        background: solid ? colour : "transparent",
        border: `1px solid ${colour}`,
        whiteSpace: "nowrap",
      }}
    >
      {children}
    </span>
  );
}

/**
 * One headline count.
 *
 * The label reads first and the number second, because the operator is looking
 * for a named quantity, not scanning anonymous digits. The value wears ink; a
 * pip beside the label carries the state, and it is absent at zero. Five
 * differently-tinted numerals in a row was the single loudest thing on the old
 * header and it said less than this does.
 *
 * Divided by a hairline rather than boxed: a rule separates without adding
 * another shape to look at.
 */
function StatTile({ label, value, colour, first }) {
  const pip = pipFor(typeof value === "number" ? value : 1, colour);
  return (
    <div
      style={{
        minWidth: 92,
        padding: "6px 16px",
        borderLeft: first ? "none" : `1px solid ${KDS.border}`,
      }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
        {pip ? (
          <span style={{ width: 6, height: 6, borderRadius: 999, background: pip, flex: "none" }} />
        ) : null}
        <span style={MICRO_LABEL}>{label}</span>
      </div>
      <div
        style={{
          ...NUM_DISPLAY,
          fontSize: 24,
          fontWeight: 700,
          lineHeight: 1.15,
          marginTop: 4,
          color: value === 0 ? KDS.textDim : KDS.text,
        }}
      >
        {value}
      </div>
    </div>
  );
}

function KitchenBoard() {
  const location = useLocation();

  // Lusuku staff open a specific kitchen with ?vendorId=. A vendor's own token
  // resolves server-side and the parameter is ignored, so there is nothing to
  // defend here beyond passing it through.
  const history = useHistory();
  const query = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const vendorIdParam = query.get("vendorId") || "";
  // Starts on the branch picked in the navbar (4 Oct 2026); staff and
  // vendors without branches start on every location, as before.
  const { outletIdParam, isBranchScoped } = useBranchScope();
  const [outletId, setOutletId] = useState(() => outletIdParam || "");

  // WHICH kitchen. A vendor has exactly one and it is selected for them. Lusuku
  // staff have every kitchen on the platform and have to choose, so the board
  // shows a picker rather than the bare "choose a restaurant" error it used to.
  const [kitchens, setKitchens] = useState([]);
  const [kitchensLoaded, setKitchensLoaded] = useState(false);
  const [pickedVendorId, setPickedVendorId] = useState("");
  const [kitchenSearch, setKitchenSearch] = useState("");
  const [showPicker, setShowPicker] = useState(false);

  const [tickets, setTickets] = useState([]);
  const [stations, setStations] = useState([]);
  const [stats, setStats] = useState(null);
  const [boardVendorId, setBoardVendorId] = useState("");
  const [stationFilter, setStationFilter] = useState("ALL");
  const [showHandedOff, setShowHandedOff] = useState(true);

  const [loading, setLoading] = useState(true);
  const [busyTicketId, setBusyTicketId] = useState(null);
  const [error, setError] = useState(null);
  const [errorCode, setErrorCode] = useState(null);
  const [lastLoadedAt, setLastLoadedAt] = useState(null);
  const [clockOffset, setClockOffset] = useState(0);
  const [nowMs, setNowMs] = useState(() => Date.now());
  const [fullscreen, setFullscreen] = useState(false);
  const [drawerTicketId, setDrawerTicketId] = useState(null);
  const [prompt, setPrompt] = useState(null);
  const [promptReason, setPromptReason] = useState("");

  const alerts = useOrderAlerts({
    soundUrl: newOrderBeep,
    repeatUntilAck: true,
    desktopTitle: "New kitchen ticket",
  });

  // `notify` is recreated by its own hook; holding it in a ref keeps the socket
  // effect from resubscribing on every render, which would drop and rejoin the
  // room several times a second. The ref is the honest fix. An eslint-disable
  // on the deps array would only hide the churn.
  const notifyRef = useRef(alerts.notify);
  useEffect(() => {
    notifyRef.current = alerts.notify;
  }, [alerts.notify]);

  // The URL wins so a board is shareable and survives a refresh; a manual pick
  // is the fallback. A vendor never reaches either branch, because their single
  // kitchen is selected for them the moment the list arrives.
  const activeVendorId = vendorIdParam || pickedVendorId;

  useEffect(() => {
    let cancelled = false;
    apiKitchenList()
      .then(({ data }) => {
        if (cancelled) return;
        const list = data?.data?.kitchens || [];
        setKitchens(list);
        setKitchensLoaded(true);
        if (!vendorIdParam && list.length === 1) {
          setPickedVendorId(String(list[0].vendorId));
        } else if (!vendorIdParam && list.length > 1) {
          setShowPicker(true);
        }
      })
      .catch(() => {
        if (!cancelled) setKitchensLoaded(true);
      });
    return () => {
      cancelled = true;
    };
  }, [vendorIdParam]);

  const activeKitchen = useMemo(
    () => kitchens.find((k) => String(k.vendorId) === String(activeVendorId)) || null,
    [kitchens, activeVendorId]
  );

  const selectKitchen = useCallback(
    (vendorId) => {
      setPickedVendorId(String(vendorId));
      setOutletId("");
      setShowPicker(false);
      // Put it in the URL so a refresh, a bookmark or a link to a colleague all
      // land on the same board.
      history.replace(`/admin/kitchen-board?vendorId=${encodeURIComponent(String(vendorId))}`);
    },
    [history]
  );

  const params = useMemo(
    () => ({
      vendorId: activeVendorId || undefined,
      outletId: outletId || undefined,
      includeClosed: 1,
      closedWindowMinutes: 45,
    }),
    [activeVendorId, outletId]
  );

  const load = useCallback(
    async (silent) => {
      // Nothing to load until a kitchen is chosen. Calling anyway is what
      // produced the endless 400s.
      if (!params.vendorId) {
        setLoading(false);
        return;
      }
      if (!silent) setLoading(true);
      try {
        const { data } = await apiKitchenBoard(params);
        const payload = data?.data || {};
        setTickets(Array.isArray(payload.tickets) ? payload.tickets : []);
        setStations(Array.isArray(payload.stations) ? payload.stations : []);
        setStats(payload.stats || null);
        setBoardVendorId(payload.vendorId ? String(payload.vendorId) : "");
        // Trust the server's clock over the browser's. A kitchen tablet with a
        // drifting clock would otherwise show every ticket as early or late.
        if (payload.serverTime) {
          setClockOffset(new Date(payload.serverTime).getTime() - Date.now());
        }
        setError(null);
        setErrorCode(null);
        setLastLoadedAt(Date.now());
      } catch (err) {
        const body = err?.response?.data;
        setError(body?.message || err?.message || "Could not load the kitchen board.");
        setErrorCode(body?.code || null);
      } finally {
        setLoading(false);
      }
    },
    [params]
  );

  useEffect(() => {
    load(false);
  }, [load]);

  // Reconcile poll. Deliberately slow: the socket carries the real traffic and
  // this exists only to repair a missed frame or a silent disconnect.
  useEffect(() => {
    if (!activeVendorId) return undefined;
    if (errorCode && TERMINAL_ERROR_CODES.includes(errorCode)) return undefined;
    const id = setInterval(() => load(true), POLL_MS);
    return () => clearInterval(id);
  }, [load, activeVendorId, errorCode]);

  // One shared ticker drives every age counter on the board. Per-tile timers
  // would mean one interval per ticket, and a busy Friday has fifty.
  useEffect(() => {
    const id = setInterval(() => setNowMs(Date.now()), TICK_MS);
    return () => clearInterval(id);
  }, []);

  /** Upsert one projected ticket from a socket event. */
  const mergeTicket = useCallback((incoming) => {
    if (!incoming?._id) return;
    setTickets((prev) => {
      const id = String(incoming._id);
      const index = prev.findIndex((t) => String(t._id) === id);
      if (index === -1) return [...prev, incoming];
      const next = prev.slice();
      next[index] = incoming;
      return next;
    });
  }, []);

  // Realtime. `kitchen:join` is authorised server-side against the vendor the
  // caller actually owns, so a guessed vendor id gets FORBIDDEN rather than
  // someone else's customer names.
  useEffect(() => {
    const vendorId = boardVendorId;
    if (!vendorId) return undefined;

    // A location narrows the join to that branch's board; without one the
    // server joins every location this login may see (4 Oct 2026).
    const join = () => socket.emit("kitchen:join", { vendorId, ...(outletId ? { location: outletId } : {}) });
    join();
    socket.on("connect", join);

    const onCreated = (ticket) => {
      mergeTicket(ticket);
      if (typeof notifyRef.current === "function") {
        notifyRef.current({
          title: "New kitchen ticket",
          body: `${ticket?.ticketCode || ticket?.orderCode || "Order"} just landed`,
        });
      }
    };
    const onUpdated = (ticket) => mergeTicket(ticket);
    const onClosed = (ticket) => mergeTicket(ticket);

    socket.on("kitchen:ticketCreated", onCreated);
    socket.on("kitchen:ticketUpdated", onUpdated);
    socket.on("kitchen:ticketClosed", onClosed);

    return () => {
      socket.off("connect", join);
      socket.off("kitchen:ticketCreated", onCreated);
      socket.off("kitchen:ticketUpdated", onUpdated);
      socket.off("kitchen:ticketClosed", onClosed);
      socket.emit("kitchen:leave", { vendorId });
    };
  }, [boardVendorId, mergeTicket, outletId]);

  useEffect(() => {
    const onChange = () => setFullscreen(Boolean(document.fullscreenElement));
    document.addEventListener("fullscreenchange", onChange);
    return () => document.removeEventListener("fullscreenchange", onChange);
  }, []);

  const ageOf = useCallback(
    (ticket) => {
      const placed = new Date(ticket?.placedAt).getTime();
      if (!Number.isFinite(placed)) return null;
      return Math.max(0, nowMs + clockOffset - placed);
    },
    [nowMs, clockOffset]
  );

  /**
   * Recompute the SLA state client-side from the server's own promise, so a
   * tile turns amber the second it should rather than on the next poll. The
   * server remains the authority: it freezes the verdict at READY and this
   * only ever affects what an open ticket looks like right now.
   */
  const liveTicket = useCallback(
    (ticket) => {
      const ageMs = ageOf(ticket);
      if (ageMs === null || !ticket?.dueAt) return { ...ticket, ageMs };
      if (["READY", "HANDED_OFF", "REJECTED", "CANCELLED"].includes(ticket.status)) {
        return { ...ticket, ageMs };
      }
      const placed = new Date(ticket.placedAt).getTime();
      const due = new Date(ticket.dueAt).getTime();
      const hard = ticket.hardDueAt ? new Date(ticket.hardDueAt).getTime() : null;
      const at = placed + ageMs;
      let state = "ON_TIME";
      if (hard && at > hard) state = "BREACHED";
      else if (at > due) state = "LATE";
      else if (ageMs >= (due - placed) * 0.75) state = "AT_RISK";
      return { ...ticket, ageMs, sla: { ...(ticket.sla || {}), state } };
    },
    [ageOf]
  );

  const visible = useMemo(() => {
    const code = String(stationFilter).toUpperCase();
    return tickets
      .map(liveTicket)
      .filter((t) => (code === "ALL" ? true : (t.stations || []).includes(code)))
      .filter((t) => (showHandedOff ? true : t.status !== "HANDED_OFF"))
      .filter((t) => !["REJECTED", "CANCELLED"].includes(t.status))
      .sort(byPriorityThenAge);
  }, [tickets, stationFilter, showHandedOff, liveTicket]);

  const lanes = useMemo(
    () =>
      LANES.map((lane) => ({
        ...lane,
        colour: STATUS_META[lane.key]?.colour || KDS.textMid,
        tickets: visible.filter((t) => t.status === lane.key),
      })),
    [visible]
  );

  const liveStats = useMemo(() => {
    const open = visible.filter((t) => t.status !== "HANDED_OFF");
    return {
      open: open.length,
      unacknowledged: open.filter((t) => t.status === "NEW").length,
      atRisk: open.filter((t) => t.sla?.state === "AT_RISK").length,
      late: open.filter((t) => t.sla?.state === "LATE").length,
      breached: open.filter((t) => t.sla?.state === "BREACHED").length,
      oldestMs: open.reduce((max, t) => Math.max(max, t.ageMs || 0), 0),
    };
  }, [visible]);

  // ── Actions ────────────────────────────────────────────────────────────────
  const runAction = useCallback(
    async (ticketId, fn, successMessage) => {
      setBusyTicketId(ticketId);
      try {
        const { data } = await fn();
        const updated = data?.data?.ticket;
        if (updated) mergeTicket(updated);
        if (successMessage) toast.success(successMessage, { autoClose: 1500 });
        return true;
      } catch (err) {
        const body = err?.response?.data;
        // A 409 is not a system failure, it is the state machine refusing an
        // impossible move (usually a second tab that already bumped this
        // ticket). Say what happened and reconcile rather than shrugging.
        if (err?.response?.status === 409) {
          toast.info(body?.message || "That ticket has already moved on.");
          load(true);
        } else {
          toast.error(body?.message || err?.message || "That did not go through.");
        }
        return false;
      } finally {
        setBusyTicketId(null);
      }
    },
    [mergeTicket, load]
  );

  const bump = useCallback(
    (ticket) => runAction(ticket._id, () => apiKitchenBumpTicket(ticket._id), null),
    [runAction]
  );

  const togglePriority = useCallback(
    (ticket) =>
      runAction(
        ticket._id,
        () =>
          apiKitchenPrioritiseTicket(ticket._id, {
            priority: ticket.priority > 0 ? 0 : 10,
            reason: ticket.priority > 0 ? "Unpinned from the front" : "Pushed to the front",
          }),
        null
      ),
    [runAction]
  );

  const toggleItem = useCallback(
    (ticket, item) => {
      const to = item.status === "DONE" ? "IN_PREP" : "DONE";
      return runAction(
        ticket._id,
        () => apiKitchenTransitionItem(ticket._id, { lineKey: item.lineKey, to }),
        null
      );
    },
    [runAction]
  );

  const openPrompt = useCallback((kind, ticket, item) => {
    setPrompt({ kind, ticket, item });
    setPromptReason("");
  }, []);

  const submitPrompt = useCallback(async () => {
    if (!prompt) return;
    const reason = promptReason.trim();
    if (!reason) {
      toast.warning("A reason is required. It goes on the permanent record for this ticket.");
      return;
    }
    const { kind, ticket, item } = prompt;
    let ok = false;
    if (kind === "reject") {
      ok = await runAction(
        ticket._id,
        () => apiKitchenTransitionTicket(ticket._id, { to: "REJECTED", reason }),
        "Ticket rejected"
      );
    } else if (kind === "recall") {
      ok = await runAction(
        ticket._id,
        () => apiKitchenRecallTicket(ticket._id, { reason }),
        "Ticket recalled"
      );
    } else if (kind === "void") {
      ok = await runAction(
        ticket._id,
        () => apiKitchenTransitionItem(ticket._id, { lineKey: item.lineKey, to: "VOIDED", reason }),
        "Line voided"
      );
    }
    if (ok) {
      setPrompt(null);
      setPromptReason("");
    }
  }, [prompt, promptReason, runAction]);

  const toggleFullscreen = useCallback(() => {
    if (document.fullscreenElement) {
      if (document.exitFullscreen) document.exitFullscreen();
    } else if (document.documentElement.requestFullscreen) {
      document.documentElement.requestFullscreen().catch(() => {
        toast.info("This browser would not go full screen. Use F11 instead.");
      });
    }
  }, []);

  const staleSeconds = lastLoadedAt ? Math.round((nowMs - lastLoadedAt) / 1000) : null;

  // ── Render ─────────────────────────────────────────────────────────────────
  const shellStyle = fullscreen
    ? { position: "fixed", inset: 0, zIndex: 3000, overflow: "hidden" }
    : { minHeight: "calc(100vh - 120px)", margin: "-15px", borderRadius: 0 };

  return (
    <div
      style={{
        ...shellStyle,
        background: KDS.bg,
        color: KDS.text,
        display: "flex",
        flexDirection: "column",
        fontFamily: "Inter, system-ui, -apple-system, sans-serif",
      }}
    >
      {/* ── Header ──────────────────────────────────────────────────────── */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 12,
          flexWrap: "wrap",
          padding: "12px 16px",
          background: KDS.bgPanel,
          borderBottom: `1px solid ${KDS.border}`,
        }}
      >
        <div style={{ marginRight: "auto", minWidth: 0 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <span style={{ fontSize: 19, fontWeight: 800, letterSpacing: 0.2 }}>
              {activeKitchen?.name || "Kitchen board"}
            </span>
            {kitchens.length > 1 ? (
              <button
                type="button"
                onClick={() => setShowPicker(true)}
                title="Open a different kitchen"
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 5,
                  padding: "4px 10px",
                  borderRadius: 999,
                  border: `1px solid ${KDS.border}`,
                  background: KDS.bgTile,
                  color: KDS.textMid,
                  fontSize: 11.5,
                  fontWeight: 700,
                  cursor: "pointer",
                }}
              >
                <MdSwapHoriz size={15} />
                Change
              </button>
            ) : null}
          </div>
          <div style={{ fontSize: 11.5, color: KDS.textDim }}>
            {!activeVendorId
              ? "No kitchen selected"
              : staleSeconds === null
              ? "Loading"
              : staleSeconds > POLL_MS / 1000 + 15
              ? `Last update ${staleSeconds}s ago. The connection may have dropped.`
              : `Live. Last update ${staleSeconds}s ago.`}
          </div>
        </div>

        {/* Gap-free so the hairlines between tiles form one continuous rule
            instead of five floating cells. */}
        <div style={{ display: "flex", alignItems: "stretch" }}>
        <StatTile label="Open" value={liveStats.open} first />
        <StatTile label="Not accepted" value={liveStats.unacknowledged} colour={KDS.new} />
        <StatTile label="Running close" value={liveStats.atRisk} colour={SLA_META.AT_RISK.colour} />
        <StatTile
          label="Late"
          value={liveStats.late + liveStats.breached}
          colour={SLA_META.LATE.colour}
        />
        <StatTile label="Oldest" value={formatDuration(liveStats.oldestMs, { compact: true })} />
        {stats?.today ? (
          <StatTile label="Cooked today" value={stats.today.cooked} colour={KDS.ready} />
        ) : null}
        </div>

        <div style={{ display: "flex", gap: 8 }}>
          <button
            type="button"
            onClick={() => {
              alerts.unlockAudio();
              alerts.toggle();
            }}
            title={alerts.enabled ? "Sound is on" : "Sound is off"}
            style={iconButtonStyle(alerts.enabled ? KDS.ready : KDS.textDim)}
          >
            {alerts.enabled ? <MdVolumeUp size={20} /> : <MdVolumeOff size={20} />}
          </button>
          <button
            type="button"
            onClick={() => load(false)}
            title="Refresh"
            style={iconButtonStyle(KDS.textMid)}
          >
            <MdRefresh size={20} />
          </button>
          <button
            type="button"
            onClick={toggleFullscreen}
            title={fullscreen ? "Leave full screen" : "Full screen"}
            style={iconButtonStyle(KDS.textMid)}
          >
            {fullscreen ? <MdFullscreenExit size={20} /> : <MdFullscreen size={20} />}
          </button>
        </div>
      </div>

      {alerts.audioBlocked ? (
        <div
          style={{
            padding: "8px 16px",
            background: KDS.dangerSoft,
            color: KDS.atRisk,
            fontSize: 12.5,
            borderBottom: `1px solid ${KDS.border}`,
          }}
        >
          <MdWarningAmber style={{ marginRight: 6 }} />
          The browser is blocking the alert sound. Tap anywhere on this page once to allow it.
        </div>
      ) : null}

      {/* ── Station filter ──────────────────────────────────────────────── */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 8,
          padding: "10px 16px",
          overflowX: "auto",
          background: KDS.bgLane,
          borderBottom: `1px solid ${KDS.border}`,
        }}
      >
        <StationTab
          code="ALL"
          label="All stations"
          colour={KDS.text}
          active={stationFilter === "ALL"}
          count={visible.length}
          onClick={() => setStationFilter("ALL")}
        />
        {stations.map((station) => (
          <StationTab
            key={station.code}
            code={station.code}
            label={station.name || station.code}
            colour={stationColour(station.code, stations)}
            active={stationFilter === station.code}
            count={
              visible.filter((t) => (t.stations || []).includes(String(station.code).toUpperCase()))
                .length
            }
            onClick={() => setStationFilter(station.code)}
          />
        ))}

        <div style={{ marginLeft: "auto", display: "flex", alignItems: "center", gap: 10 }}>
          <label
            style={{
              display: "flex",
              alignItems: "center",
              gap: 6,
              fontSize: 12,
              color: KDS.textMid,
              margin: 0,
              cursor: "pointer",
            }}
          >
            <input
              type="checkbox"
              checked={showHandedOff}
              onChange={(e) => setShowHandedOff(e.target.checked)}
            />
            Show handed off
          </label>
          <AppSelect
            value={outletId}
            onChange={(e) => setOutletId(e.target.value)}
            style={{
              background: KDS.bgTile,
              color: KDS.text,
              border: `1px solid ${KDS.border}`,
              borderRadius: KDS.radiusSm,
              padding: "6px 10px",
              fontSize: 12.5,
            }}
          >
            <option value="">{isBranchScoped ? "All my branches" : "All locations"}</option>
            {/* A member whose branches leave out the main store is not offered it. */}
            {activeKitchen?.includesMain !== false && <option value="main">Main store only</option>}
            {(activeKitchen?.outlets || []).map((outlet) => (
              <option key={outlet._id} value={outlet._id}>
                {outlet.name}
              </option>
            ))}
          </AppSelect>
        </div>
      </div>

      {/* ── Lanes ───────────────────────────────────────────────────────── */}
      {!activeVendorId || showPicker ? (
        <KitchenPicker
          kitchens={kitchens}
          loaded={kitchensLoaded}
          search={kitchenSearch}
          onSearch={setKitchenSearch}
          onSelect={selectKitchen}
          onCancel={activeVendorId ? () => setShowPicker(false) : null}
        />
      ) : error ? (
        <div style={{ padding: 40, textAlign: "center", color: KDS.danger }}>
          <div style={{ fontSize: 16, fontWeight: 700, marginBottom: 6 }}>{error}</div>
          <Button variant="outline-light" size="sm" onClick={() => load(false)}>
            Try again
          </Button>
        </div>
      ) : loading && !tickets.length ? (
        <div style={{ padding: 60, textAlign: "center", color: KDS.textDim }}>
          Loading the board
        </div>
      ) : !visible.length ? (
        <div style={{ padding: 80, textAlign: "center" }}>
          <div style={{ fontSize: 22, fontWeight: 700, color: KDS.textMid }}>Nothing on the pass</div>
          <div style={{ fontSize: 13.5, color: KDS.textDim, marginTop: 8 }}>
            New tickets land here the moment an order is placed, from the app, the website,
            WhatsApp, the call centre or a POS terminal.
          </div>
        </div>
      ) : (
        <div
          style={{
            flex: 1,
            display: "grid",
            gridTemplateColumns: `repeat(${LANES.length}, minmax(280px, 1fr))`,
            gap: 10,
            padding: 12,
            overflowX: "auto",
            overflowY: "hidden",
            // Lanes fill the board's height so the rules between them run top
            // to bottom, and a long lane scrolls inside itself (minHeight 0 on
            // the lane and its body) instead of being cut off by the board.
            alignItems: "stretch",
          }}
        >
          {lanes.map((lane, laneIndex) => (
            <div
              key={lane.key}
              style={{
                // No outline. The lane is defined by its header rule, by the
                // space around it and by the perforated rule on its left, not
                // by a box drawn inside another box. On a dark ground the
                // lane's own tint barely separates from the gutter (Izi, 26 Sep
                // 2026), so the rule is what tells New from Accepted at a
                // glance. Dashed, not solid: a solid hairline in the lane's
                // border colour vanished against the ground; the perforation
                // reads as a divider without becoming a box edge.
                background: KDS.bgLane,
                borderRadius: KDS.radius,
                borderLeft: laneIndex === 0 ? "none" : `1px dashed ${KDS.textDim}`,
                display: "flex",
                flexDirection: "column",
                maxHeight: "100%",
                minHeight: 120,
              }}
            >
              <div
                style={{
                  padding: "10px 12px",
                  borderBottom: `1px solid ${KDS.border}`,
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                }}
              >
                <span
                  style={{
                    width: 9,
                    height: 9,
                    borderRadius: 999,
                    background: lane.colour,
                    flexShrink: 0,
                  }}
                />
                <span style={{ ...MICRO_LABEL, color: KDS.textMid, fontSize: 11 }}>
                  {lane.label}
                </span>
                <span style={{ marginLeft: "auto", color: KDS.textDim, fontSize: 13, fontWeight: 700 }}>
                  {lane.tickets.length}
                </span>
              </div>

              <div style={{ padding: 10, display: "grid", gap: 10, overflowY: "auto", flex: 1, minHeight: 0, alignContent: "start" }}>
                {lane.tickets.map((ticket) => (
                  <TicketTile
                    key={ticket._id}
                    ticket={ticket}
                    stations={stations}
                    busy={busyTicketId === ticket._id}
                    onBump={() => bump(ticket)}
                    onRecall={() => openPrompt("recall", ticket)}
                    onReject={() => openPrompt("reject", ticket)}
                    onVoidItem={(item) => openPrompt("void", ticket, item)}
                    onToggleItem={(item) => toggleItem(ticket, item)}
                    onTogglePriority={() => togglePriority(ticket)}
                    onOpenDetail={() => setDrawerTicketId(ticket._id)}
                  />
                ))}
                {!lane.tickets.length ? (
                  <div style={{ padding: "18px 6px", textAlign: "center", color: KDS.textDim, fontSize: 12 }}>
                    Empty
                  </div>
                ) : null}
              </div>
            </div>
          ))}
        </div>
      )}

      <KitchenTicketDrawer
        ticketId={drawerTicketId}
        open={Boolean(drawerTicketId)}
        onClose={() => setDrawerTicketId(null)}
        onChanged={() => load(true)}
      />

      <ReasonPrompt
        prompt={prompt}
        reason={promptReason}
        onReason={setPromptReason}
        onCancel={() => setPrompt(null)}
        onSubmit={submitPrompt}
      />
    </div>
  );
}

/**
 * Which kitchen. Only ever seen by Lusuku staff, who have more than one.
 *
 * This replaces the screen's original dead end: the board asked the API for a
 * kitchen it had not been told to open, got a 400 back, rendered the server's
 * "choose a restaurant" message as a red error with a Try again button that
 * could only ever fail the same way, and kept polling. An operator cannot
 * choose anything from an error, so here is the list.
 *
 * Sorted worst first, matching the operations board: the kitchen with breaches
 * is the one someone is looking for.
 */
function KitchenPicker({ kitchens, loaded, search, onSearch, onSelect, onCancel }) {
  const term = String(search || "").trim().toLowerCase();
  const visible = kitchens
    .filter((k) => (term ? String(k.name || "").toLowerCase().includes(term) : true))
    .slice()
    .sort((a, b) => {
      if ((b.breachedTickets || 0) !== (a.breachedTickets || 0)) {
        return (b.breachedTickets || 0) - (a.breachedTickets || 0);
      }
      if ((b.openTickets || 0) !== (a.openTickets || 0)) {
        return (b.openTickets || 0) - (a.openTickets || 0);
      }
      return String(a.name || "").localeCompare(String(b.name || ""));
    });

  return (
    <div style={{ flex: 1, overflowY: "auto", padding: "28px 20px 40px" }}>
      <div style={{ maxWidth: 760, margin: "0 auto" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 6 }}>
          <MdRestaurant size={22} color={KDS.info} />
          <span style={{ fontSize: 20, fontWeight: 800 }}>Open a kitchen</span>
          {onCancel ? (
            <button
              type="button"
              onClick={onCancel}
              style={{
                marginLeft: "auto",
                background: "none",
                border: "none",
                color: KDS.textMid,
                cursor: "pointer",
              }}
              title="Keep the current kitchen"
            >
              <MdClose size={20} />
            </button>
          ) : null}
        </div>
        <div style={{ fontSize: 13, color: KDS.textDim, marginBottom: 18 }}>
          A board shows one kitchen at a time. For every kitchen at once, use Live Kitchens.
        </div>

        <input
          value={search}
          onChange={(e) => onSearch(e.target.value)}
          placeholder="Search restaurants"
          style={{
            width: "100%",
            padding: "11px 14px",
            marginBottom: 14,
            borderRadius: KDS.radiusSm,
            border: `1px solid ${KDS.border}`,
            background: KDS.bgTile,
            color: KDS.text,
            fontSize: 14,
          }}
        />

        {!loaded ? (
          <div style={{ padding: 30, textAlign: "center", color: KDS.textDim }}>
            Loading kitchens
          </div>
        ) : !kitchens.length ? (
          <div style={{ padding: 30, textAlign: "center", color: KDS.textDim }}>
            No restaurants are available to this account.
          </div>
        ) : !visible.length ? (
          <div style={{ padding: 30, textAlign: "center", color: KDS.textDim }}>
            No restaurant matches that search.
          </div>
        ) : (
          <div style={{ display: "grid", gap: 8 }}>
            {visible.map((kitchen) => (
              <button
                key={kitchen.vendorId}
                type="button"
                onClick={() => onSelect(kitchen.vendorId)}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 12,
                  width: "100%",
                  minHeight: 62,
                  padding: "10px 14px",
                  textAlign: "left",
                  borderRadius: KDS.radiusSm,
                  border: `1px solid ${KDS.border}`,
                  borderLeft: `4px solid ${
                    kitchen.breachedTickets ? KDS.breached : kitchen.openTickets ? KDS.preparing : KDS.border
                  }`,
                  background: KDS.bgTile,
                  color: KDS.text,
                  cursor: "pointer",
                }}
              >
                <div style={{ minWidth: 0, flex: 1 }}>
                  <div style={{ fontSize: 15, fontWeight: 700 }}>{kitchen.name}</div>
                  <div style={{ fontSize: 11.5, color: KDS.textDim, marginTop: 2 }}>
                    {kitchen.outlets?.length
                      ? `Main store and ${kitchen.outlets.length} outlet${kitchen.outlets.length === 1 ? "" : "s"}`
                      : "Main store"}
                    {kitchen.promisedPrepMinutes ? ` · promises ${kitchen.promisedPrepMinutes} min` : ""}
                    {kitchen.country ? ` · ${kitchen.country}` : ""}
                  </div>
                </div>
                <div style={{ display: "flex", gap: 6 }}>
                  {kitchen.breachedTickets ? (
                    <Pill colour={KDS.breached} solid>
                      {kitchen.breachedTickets} breached
                    </Pill>
                  ) : null}
                  <Pill colour={kitchen.openTickets ? KDS.preparing : KDS.textDim}>
                    {kitchen.openTickets} open
                  </Pill>
                </div>
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function iconButtonStyle(colour) {
  return {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    width: 40,
    height: 40,
    borderRadius: KDS.radiusSm,
    background: KDS.bgTile,
    border: `1px solid ${KDS.border}`,
    color: colour,
    cursor: "pointer",
  };
}

function StationTab({ code, label, colour, active, count, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 7,
        padding: "8px 14px",
        minHeight: 40,
        borderRadius: 999,
        border: `1px solid ${active ? colour : KDS.border}`,
        background: active ? `${colour}22` : KDS.bgTile,
        color: active ? KDS.text : KDS.textMid,
        fontSize: 12.5,
        fontWeight: 700,
        whiteSpace: "nowrap",
        cursor: "pointer",
      }}
    >
      <span style={{ width: 8, height: 8, borderRadius: 999, background: colour }} />
      {label}
      <span style={{ color: KDS.textDim, fontWeight: 800 }}>{count}</span>
      <span style={{ display: "none" }}>{code}</span>
    </button>
  );
}

/**
 * One ticket. The tile is deliberately dense at the top (what to cook) and
 * quiet at the bottom (who it is for), because a cook reads top-down and stops
 * as soon as they have what they need.
 */
function TicketTile({
  ticket,
  stations,
  busy,
  onBump,
  onRecall,
  onReject,
  onVoidItem,
  onToggleItem,
  onTogglePriority,
  onOpenDetail,
}) {
  const edge = ageColour(ticket);
  const slaMeta = SLA_META[ticket.sla?.state] || SLA_META.ON_TIME;
  const statusMeta = STATUS_META[ticket.status] || {};
  const rollup = ticket.rollup || {};
  const cookable = rollup.cookable || 0;

  return (
    <div
      style={{
        background: KDS.bgTile,
        border: `1px solid ${KDS.border}`,
        borderLeft: `5px solid ${edge}`,
        borderRadius: KDS.radiusSm,
        overflow: "hidden",
        opacity: busy ? 0.6 : 1,
        transition: "opacity 120ms ease",
      }}
    >
      {/* Head */}
      <div style={{ padding: "10px 12px 8px", display: "flex", alignItems: "flex-start", gap: 8 }}>
        <div style={{ minWidth: 0 }}>
          <div style={{ fontSize: 22, fontWeight: 900, letterSpacing: 0.5, lineHeight: 1.05 }}>
            {ticket.ticketCode || ticket.orderCode}
          </div>
          <div style={{ fontSize: 11, color: KDS.textDim, marginTop: 2 }}>
            {ticket.orderCode}
            {ticket.outletName ? ` · ${ticket.outletName}` : ""}
          </div>
        </div>

        <div style={{ marginLeft: "auto", textAlign: "right" }}>
          <div style={{ fontSize: 20, fontWeight: 800, color: edge, lineHeight: 1.05 }}>
            {formatDuration(ticket.ageMs, { compact: true })}
          </div>
          <div style={{ fontSize: 10, color: KDS.textDim }}>
            {ticket.promisedPrepMinutes ? `of ${ticket.promisedPrepMinutes}m` : formatClock(ticket.placedAt)}
          </div>
        </div>
      </div>

      {/* Badges */}
      <div style={{ padding: "0 12px 8px", display: "flex", flexWrap: "wrap", gap: 5 }}>
        <Pill colour={statusMeta.colour || KDS.textMid}>{statusMeta.label || ticket.status}</Pill>
        <Pill colour={slaMeta.colour}>{slaMeta.label}</Pill>
        {ticket.priority > 0 ? <Pill colour={KDS.info} solid>Front of queue</Pill> : null}
        {!ticket.isPaid ? <Pill colour={KDS.atRisk}>Unpaid</Pill> : null}
        {ticket.isMultiVendorOrder ? (
          <Pill colour={KDS.textDim} title="This customer also ordered from another vendor">
            Split order
          </Pill>
        ) : null}
        {(ticket.stations || []).map((code) => (
          <Pill key={code} colour={stationColour(code, stations)}>
            {code}
          </Pill>
        ))}
      </div>

      {/* Items */}
      <div style={{ borderTop: `1px solid ${KDS.border}` }}>
        {(ticket.items || []).map((item) => {
          const done = item.status === "DONE";
          const voided = item.status === "VOIDED";
          const meta = ITEM_STATUS_META[item.status] || ITEM_STATUS_META.PENDING;
          const options = describeOptions(item);
          return (
            <div
              key={item.lineKey}
              style={{
                display: "flex",
                alignItems: "flex-start",
                gap: 9,
                padding: "9px 12px",
                borderBottom: `1px solid ${KDS.bgLane}`,
                background: voided ? "rgba(239,68,68,0.07)" : "transparent",
              }}
            >
              <button
                type="button"
                disabled={voided || busy}
                onClick={() => onToggleItem(item)}
                title={done ? "Mark as still cooking" : "Mark this line done"}
                style={{
                  background: "none",
                  border: "none",
                  padding: 0,
                  marginTop: 1,
                  color: done ? KDS.ready : KDS.textDim,
                  cursor: voided ? "default" : "pointer",
                  minWidth: 26,
                  minHeight: 26,
                }}
              >
                {done ? <MdCheckCircle size={22} /> : <MdRadioButtonUnchecked size={22} />}
              </button>

              <div style={{ minWidth: 0, flex: 1 }}>
                <div
                  style={{
                    fontSize: 15,
                    fontWeight: 700,
                    color: voided ? KDS.textDim : KDS.text,
                    textDecoration: voided ? "line-through" : "none",
                  }}
                >
                  <span style={{ color: KDS.info, marginRight: 6 }}>{item.qty}x</span>
                  {item.name}
                </div>
                {options.length ? (
                  <div style={{ marginTop: 4, display: "flex", flexWrap: "wrap", gap: 4 }}>
                    {options.map((opt, i) => (
                      <span
                        key={`${item.lineKey}-opt-${i}`}
                        style={{
                          fontSize: 11,
                          color: KDS.textMid,
                          background: KDS.bgLane,
                          border: `1px solid ${KDS.border}`,
                          borderRadius: 4,
                          padding: "1px 6px",
                        }}
                      >
                        {opt}
                      </span>
                    ))}
                  </div>
                ) : null}
                {item.notes ? (
                  <div style={{ marginTop: 4, fontSize: 11.5, color: KDS.atRisk }}>
                    Note: {item.notes}
                  </div>
                ) : null}
                {voided && item.voidReason ? (
                  <div style={{ marginTop: 4, fontSize: 11.5, color: KDS.danger }}>
                    Voided: {item.voidReason}
                  </div>
                ) : null}
              </div>

              <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                <span
                  style={{
                    fontSize: 10,
                    fontWeight: 700,
                    color: stationColour(item.station, stations),
                    whiteSpace: "nowrap",
                  }}
                  title={`Routed by ${item.stationSource}`}
                >
                  {item.station}
                </span>
                <span style={{ display: "none" }}>{meta.label}</span>
                {!voided ? (
                  <button
                    type="button"
                    disabled={busy}
                    onClick={() => onVoidItem(item)}
                    title="Void this line"
                    style={{
                      background: "none",
                      border: "none",
                      color: KDS.textDim,
                      cursor: "pointer",
                      minWidth: 26,
                      minHeight: 26,
                    }}
                  >
                    <MdClose size={16} />
                  </button>
                ) : null}
              </div>
            </div>
          );
        })}
      </div>

      {/* Customer strip */}
      <div style={{ padding: "8px 12px", fontSize: 11.5, color: KDS.textDim, display: "grid", gap: 2 }}>
        <div>
          {ticket.customerName || "Customer"}
          {ticket.deliveryArea ? ` · ${ticket.deliveryArea}` : ""}
        </div>
        <div>
          {ticket.paymentMethod || "Payment pending"} · {formatMoney(ticket.totalPrice, ticket.currency)}
          {ticket.lastKnownDeliveryStatus ? ` · delivery ${ticket.lastKnownDeliveryStatus}` : ""}
        </div>
        {cookable ? (
          <div>
            {rollup.done || 0} of {cookable} lines done
            {rollup.voided ? `, ${rollup.voided} voided` : ""}
          </div>
        ) : null}
      </div>

      {/* Actions */}
      <div
        style={{
          display: "flex",
          alignItems: "stretch",
          gap: 6,
          padding: 8,
          borderTop: `1px solid ${KDS.border}`,
          background: KDS.bgLane,
        }}
      >
        {ticket.nextStatus ? (
          <button
            type="button"
            disabled={busy}
            onClick={onBump}
            style={{
              flex: 1,
              minHeight: 46,
              border: "none",
              borderRadius: KDS.radiusSm,
              // ONE action colour for the whole board. Tinting the primary
              // button by the ticket's next stage meant the same button was a
              // different colour on every card, which reads as five different
              // products rather than one queue. Stage is carried by the lane
              // the card sits in and by the words on the button.
              background: KDS.action,
              color: KDS.actionInk,
              fontSize: 14,
              fontWeight: 900,
              letterSpacing: 0.3,
              cursor: "pointer",
            }}
          >
            {STATUS_META[ticket.status]?.action || `Move to ${ticket.nextStatus}`}
          </button>
        ) : (
          <div style={{ flex: 1, alignSelf: "center", color: KDS.textDim, fontSize: 12, paddingLeft: 4 }}>
            Kitchen work complete
          </div>
        )}

        {ticket.recallTo ? (
          <button
            type="button"
            disabled={busy}
            onClick={onRecall}
            title="Undo the last step"
            style={secondaryButtonStyle()}
          >
            <MdUndo size={18} />
          </button>
        ) : null}

        <button
          type="button"
          disabled={busy}
          onClick={onTogglePriority}
          title={ticket.priority > 0 ? "Remove from the front of the queue" : "Push to the front"}
          style={secondaryButtonStyle(ticket.priority > 0 ? KDS.info : KDS.textMid)}
        >
          <MdPushPin size={18} />
        </button>

        {["NEW", "ACKNOWLEDGED"].includes(ticket.status) ? (
          <button
            type="button"
            disabled={busy}
            onClick={onReject}
            title="Reject this ticket"
            style={secondaryButtonStyle(KDS.danger)}
          >
            <MdClose size={18} />
          </button>
        ) : null}

        <button
          type="button"
          onClick={onOpenDetail}
          title="Ticket detail and audit trail"
          style={secondaryButtonStyle()}
        >
          <MdInfoOutline size={18} />
        </button>
      </div>
    </div>
  );
}

function secondaryButtonStyle(colour) {
  return {
    minWidth: 46,
    minHeight: 46,
    borderRadius: KDS.radiusSm,
    border: `1px solid ${KDS.border}`,
    background: KDS.bgTile,
    color: colour || KDS.textMid,
    cursor: "pointer",
  };
}

/**
 * One prompt for all three reason-carrying actions. The server rejects any of
 * them without a reason, so this is not a nicety: it is the client half of a
 * server rule, and it offers the common answers so the reason field does not
 * quietly fill up with a single space.
 */
function ReasonPrompt({ prompt, reason, onReason, onCancel, onSubmit }) {
  const kind = prompt?.kind;
  const presets =
    kind === "void" ? KITCHEN_VOID_REASONS : kind === "reject" ? KITCHEN_REJECT_REASONS : [];

  const title =
    kind === "reject"
      ? "Reject this ticket"
      : kind === "recall"
      ? "Recall this ticket"
      : "Void this line";

  const explanation =
    kind === "reject"
      ? "The customer will need to be told. This is recorded against the kitchen."
      : kind === "recall"
      ? "This undoes the last step. Recalls are counted separately from normal bumps, so say what happened."
      : "Voiding removes this line from what the kitchen owes. It affects the bill and the vendor's numbers.";

  return (
    <Modal show={Boolean(prompt)} onHide={onCancel} centered>
      <Modal.Header closeButton>
        <Modal.Title>{title}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <p style={{ fontSize: 13.5, color: "#4a5568" }}>{explanation}</p>
        {prompt?.item ? (
          <p style={{ fontWeight: 700, marginBottom: 10 }}>
            {prompt.item.qty}x {prompt.item.name}
          </p>
        ) : null}
        {presets.length ? (
          <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginBottom: 10 }}>
            {presets.map((p) => (
              <Button
                key={p}
                size="sm"
                variant={reason === p ? "primary" : "outline-secondary"}
                onClick={() => onReason(p)}
              >
                {p}
              </Button>
            ))}
          </div>
        ) : null}
        <Form.Control
          as="textarea"
          rows={2}
          value={reason}
          placeholder="Reason"
          onChange={(e) => onReason(e.target.value)}
        />
      </Modal.Body>
      <Modal.Footer>
        <Button variant="light" onClick={onCancel}>
          Cancel
        </Button>
        <Button variant={kind === "recall" ? "primary" : "danger"} onClick={onSubmit}>
          Confirm
        </Button>
      </Modal.Footer>
    </Modal>
  );
}

export default KitchenBoard;
